import '../../../styles/pages.css'

import React, { useState } from 'react'

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material'
import RequestDialog from '../../dialogs/RequestDialog'
import { useWithErrorNotificationStatus } from 'shared/ui/GlobalDialog'
import { makeKiddomPostRequest } from 'infrastructure/api'

interface SyncSISButtonProps {
  name: string
  districtSISID?: string
  districtID?: number
  schoolID?: number
  automatedOffboarding?: boolean
}

const SyncSISButton = ({
  name,
  districtSISID,
  districtID,
  schoolID,
  automatedOffboarding,
}: SyncSISButtonProps) => {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  const [showBypassDialog, setShowBypassDialog] = useState(false)
  const [bypassInput, setBypassInput] = useState('')
  const [bypassError, setBypassError] = useState('')
  const [bypassThreshold, setBypassThreshold] = useState(false)

  const doDistrictSync = useWithErrorNotificationStatus(
    { errorTitle: 'Error syncing the district' },
    () => {
      return makeKiddomPostRequest({
        kiddomPath: `/v1/sis/ingestion/sync?district_sisid=${districtSISID}`,
        requestBody: {
          district_id: districtID,
          bypass_threshold: bypassThreshold,
        },
      })
    },
  )
  const doSchoolSync = useWithErrorNotificationStatus(
    { errorTitle: 'Error syncing the school' },
    () => {
      return makeKiddomPostRequest({
        kiddomPath: `/v1/sis/ingestion/sync?school_id=${schoolID}`,
        requestBody: {},
      })
    },
  )

  const entityType = schoolID ? 'School' : 'District'
  const title = `Are you sure you want to sync the ${entityType.toLowerCase()}?`
  const content = [
    {
      key: `${entityType} Name`,
      value: name,
    },
    {
      key: `${entityType} ID`,
      value: schoolID ? String(schoolID) : String(districtID),
    },
  ]

  const handleBypassSubmit = () => {
    if (bypassInput === 'bypass') {
      setBypassThreshold(true)
      setShowBypassDialog(false)
      setOpen(true)
      setBypassInput('')
      setBypassError('')
    } else if (bypassInput === '') {
      setBypassThreshold(false)
      setShowBypassDialog(false)
      setOpen(true)
      setBypassInput('')
      setBypassError('')
    } else {
      setBypassError('Please type "bypass" to proceed.')
    }
  }

  const handleSyncButtonClick = () => {
    if (automatedOffboarding) {
      setShowBypassDialog(true) // If automated offboarding is true, show the bypass dialog first
    } else {
      setOpen(true) // Otherwise, directly show the primary confirmation dialog
    }
  }

  const syncDistrict = async () => {
    if (districtSISID) {
      try {
        setError('')
        const success = await doDistrictSync()
        if (!success) {
          setError('Error syncing the district')
        }
      } catch {
        setError('Error syncing the district')
      } finally {
        setBypassThreshold(false)
      }
    } else {
      setError('Invalid Sync ID')
    }
  }

  const syncSchool = async () => {
    try {
      setError('')
      const success = await doSchoolSync()
      if (!success) {
        setError('Error syncing the school')
      }
    } catch {
      setError('Error syncing the school')
    } finally {
      setBypassThreshold(false)
    }
  }

  return (
    <div className="section-buttons">
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleSyncButtonClick}
      >
        {`Sync ${entityType}`}
      </Button>
      {error && <p className="error">{error}</p>}

      <Dialog
        open={showBypassDialog}
        onClose={() => {
          setShowBypassDialog(false)
          setBypassInput('')
          setBypassError('')
        }}
      >
        <DialogTitle>Automated Offboarding Protection is active.</DialogTitle>
        <DialogContent>
          <p>
            This sync is protected and will not delete more than 10% of users in
            any school. If you want to keep this protection, just click
            "Confirm".
          </p>
          <p>
            If you want to bypass this protection, type <b>"bypass"</b> into the
            field below then click "Confirm".
          </p>
          <TextField
            autoFocus
            margin="dense"
            id="bypass-input"
            label="Type 'bypass'"
            type="text"
            fullWidth
            variant="outlined"
            value={bypassInput}
            onChange={(e) => setBypassInput(e.target.value)}
            error={!!bypassError}
            helperText={bypassError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowBypassDialog(false)
              setBypassInput('')
              setBypassError('')
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBypassSubmit}
            disabled={bypassInput !== 'bypass' && bypassInput !== ''}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <RequestDialog
        open={open}
        title={title}
        content={content}
        submitRequest={schoolID ? syncSchool : syncDistrict}
        waitingTime={2000}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

export default SyncSISButton
